/**
 * IXVerify v2 — session card (dashboard queue + recent lists; ixv2 WP-3.2).
 * Renders the PHI-free session summary only (R3 — no subject data rides
 * lists). Status pills pair icon + text (color never carries meaning alone).
 */
import {
  CheckCircle2,
  CircleDashed,
  Clock,
  FileCheck2,
  ShieldAlert,
  XCircle,
  Camera,
} from 'lucide-react';
import type { IxvSessionSummary, SessionStatus } from '../../lib/api';

const STATUS_META: Record<SessionStatus, { label: string; cls: string; Icon: any }> = {
  draft: { label: 'Draft', cls: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300', Icon: CircleDashed },
  staged: { label: 'Ready to dispense', cls: 'bg-primary-100 text-primary-800 dark:bg-primary-900/40 dark:text-primary-300', Icon: Clock },
  capturing: { label: 'Capturing', cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300', Icon: Camera },
  review: { label: 'In review', cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300', Icon: CircleDashed },
  completed: { label: 'Published', cls: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300', Icon: CheckCircle2 },
  second_check_pending: { label: 'Second check pending', cls: 'bg-primary-100 text-primary-800 dark:bg-primary-900/40 dark:text-primary-300', Icon: FileCheck2 },
  verified: { label: 'Verified', cls: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300', Icon: CheckCircle2 },
  mismatch_recorded: { label: 'Mismatch recorded', cls: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300', Icon: ShieldAlert },
  cancelled: { label: 'Cancelled', cls: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300', Icon: XCircle },
};

export function SessionStatusPill({ status }: { status: SessionStatus }) {
  const meta = STATUS_META[status] || STATUS_META.draft;
  const { Icon } = meta;
  return (
    <span className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium ${meta.cls}`}>
      <Icon className="h-3.5 w-3.5" aria-hidden="true" />
      {meta.label}
    </span>
  );
}

function fmtWhen(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export default function SessionCard({
  session,
  onOpen,
  highlight = false,
  protocol = null,
}: {
  session: IxvSessionSummary;
  onOpen: (session: IxvSessionSummary) => void;
  highlight?: boolean;
  /** Study protocol label (WP-3.3 — the dashboard resolves it from the study list). */
  protocol?: string | null;
}) {
  return (
    <button
      type="button"
      onClick={() => onOpen(session)}
      className={`flex w-full items-center justify-between gap-3 rounded-xl border bg-white p-4 text-left transition-all duration-150 hover:border-gray-300 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary-500 motion-reduce:transition-none dark:bg-gray-800 dark:hover:border-gray-600 ${
        highlight ? 'border-primary-300 dark:border-primary-800' : 'border-gray-200 dark:border-gray-700'
      }`}
    >
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <SessionStatusPill status={session.status} />
          {session.verification_code && (
            <span className="text-xs font-medium tabular-nums text-gray-500 dark:text-gray-400">{session.verification_code}</span>
          )}
        </div>
        <div className="mt-1 truncate text-sm text-gray-900 dark:text-gray-100">
          {protocol ? <span className="font-medium">{protocol} · </span> : null}
          {session.visit_name || 'Dispense session'}
          {session.dispense_date ? <span className="text-gray-500 dark:text-gray-400"> · {session.dispense_date}</span> : null}
        </div>
        <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
          {session.status === 'staged' ? `Staged ${fmtWhen(session.staged_at)}` : fmtWhen(session.created_at)}
        </div>
      </div>
    </button>
  );
}
